import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  apifetchCurrentUserNotifications,
  apiMarkNotificationAsRead,
  apiMarkAllNotificationsAsRead,
} from "../../api/backend/user";
import { useAuth } from "./authContext";
import { writeUnreadBadge, clearUnreadBadge } from "../../utils/notifications";

const NotificationContext = createContext();

// ===== LOG HELPERS =====
const log = (...args) => console.log("[Notifications]", ...args);
const warn = (...args) => console.warn("[Notifications]", ...args);
const err = (...args) => console.error("[Notifications]", ...args);

export const NotificationProvider = ({ children }) => {
  const { currentMemberId, authReady } = useAuth();
  const loggedInAccountHolderId = currentMemberId ?? null;
  const [notifications, setNotifications] = useState([]);
  const [unreadLiveCount, setUnreadLiveCount] = useState(0);
  const [dbSnapShotUnreadCount, setDbSnapShotUnreadCount] = useState(null);
  const notificationUnreadBadgeCount = dbSnapShotUnreadCount ?? unreadLiveCount;

  const normalize = useCallback(
    (n) => {
      if (loggedInAccountHolderId == null) {
        return {
          ...n,
          loggedInAccountHolderId: null,
          isTarget: false,
          isSource: false,
          otherUser: null,
        };
      }
      const loggedInIdStr = String(loggedInAccountHolderId); // <-- rename
      const isTarget =
        n?.targetId != null && String(n.targetId) === loggedInIdStr;
      const isSource =
        n?.sourceId != null && String(n.sourceId) === loggedInIdStr;
      const otherUser = isTarget ? (n?.source ?? null) : (n?.target ?? null);
      return {
        ...n,
        loggedInAccountHolderId: loggedInIdStr,
        isTarget,
        isSource,
        otherUser,
      };
    },
    [loggedInAccountHolderId]
  );

  const sortByCreatedDesc = (a, b) =>
    new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0);

  const fetchNotifications = useCallback(async () => {
    if (!loggedInAccountHolderId) {
      warn("fetchNotifications without a user id");
      return;
    }
    console.group("[Notifications] fetch");
    log("start", { loggedInAccountHolderId });
    try {
      console.time("[Notifications] api:apifetchCurrentUserNotifications");
      const response = await apifetchCurrentUserNotifications({
        loggedInAccountHolderId,
      });
      console.timeEnd("[Notifications] api:apifetchCurrentUserNotifications");
      const acctHldrNotificationsList = Array.isArray(response.data)
        ? response.data
        : [];
      const normalizedAcctHldrNotificationsList = acctHldrNotificationsList
        .map(normalize)
        .sort(sortByCreatedDesc);
      setNotifications(normalizedAcctHldrNotificationsList);
      setUnreadLiveCount(normalizedAcctHldrNotificationsList.length); // keep list length in sync
      log("set state", {
        unreadLiveCount: normalizedAcctHldrNotificationsList.length,
      });
    } catch (e) {
      err("fetch error", {
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
      });
    } finally {
      console.groupEnd();
    }
  }, [loggedInAccountHolderId, normalize]);

  useEffect(() => {
    if (!loggedInAccountHolderId || !authReady) return;
    fetchNotifications();
  }, [loggedInAccountHolderId, authReady, fetchNotifications]);

  useEffect(() => {
    if (loggedInAccountHolderId) return;
    setNotifications([]);
    setUnreadLiveCount(0);
    setDbSnapShotUnreadCount(null);
  }, [loggedInAccountHolderId]);
  

  // From middleware push
  const notificationsCountFetchedFromMiddleware = useCallback(
    (count) => {
      writeUnreadBadge(loggedInAccountHolderId, count);
      pushSnapshot(count);
    },
    [loggedInAccountHolderId]
  );

  const pushSnapshot = useCallback(
    (count) => {
      const n = Math.max(0, Number(count) || 0);
      localStorage.setItem(
        `skitsicon.unread.badge.${loggedInAccountHolderId}`,
        String(n)
      );
      setDbSnapShotUnreadCount(n); // <-- this “switches” the badge
    },
    [loggedInAccountHolderId]
  );

  // When bell opens:
  const clearSnapshot = useCallback(() => {
    localStorage.removeItem(
      `skitsicon.unread.badge.${loggedInAccountHolderId}`
    );
    setDbSnapShotUnreadCount(null); // <-- badge falls back to live
  }, [loggedInAccountHolderId]);

  // === When bell opens: switch back to LIST mode and refresh ===
  const onBellOpen = useCallback(async () => {
    clearSnapshot(); // sets dbSnapShotUnreadCount -> null & clears LS
    await fetchNotifications(); // sets unreadLiveCount from the list length
  }, [clearSnapshot, fetchNotifications]);

  const markNotificationAsRead = async (notificationId) => {
    console.group("[Notifications] markAsRead");
    log("request", { notificationId });
    console.time("[Notifications] api:apiMarkNotificationAsRead");
    try {
      await apiMarkNotificationAsRead({
        loggedInAccountHolderId,
        notificationId,
      });
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setUnreadLiveCount((prev) => {
        const next = Math.max(0, prev - 1);
        log("unreadLiveCount ->", { prev, next });
        return next;
      });

      log("success");
    } catch (error) {
      err("markAsRead error", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
    } finally {
      console.timeEnd("[Notifications] api:apiMarkNotificationAsRead");
      console.groupEnd();
    }
  };
  const clearAllNotifications = async () => {
    console.group("[Notifications] clearAll");
    log("request", { loggedInAccountHolderId });
    console.time("[Notifications] api:apiMarkAllNotificationsAsRead");
    try {
      await apiMarkAllNotificationsAsRead({ loggedInAccountHolderId });
      setNotifications([]);
      setUnreadLiveCount(0);
      log("cleared");
    } catch (error) {
      err("clearAll error", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
    } finally {
      console.timeEnd("[Notifications] api:apiMarkAllNotificationsAsRead");
      console.groupEnd();
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        notificationUnreadBadgeCount,
        onBellOpen,
        fetchNotifications,
        notificationsCountFetchedFromMiddleware,
        markNotificationAsRead,
        clearAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
